import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  AlertTriangle,
  Plus,
  ArrowLeft,
  Calendar,
  DollarSign,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  Camera,
  Sparkles,
  ChevronRight,
  AlertCircle,
  Eye,
  History,
  Gavel,
  Archive,
  RotateCcw,
  Trash2,
  ArrowUpCircle,
  Loader2 as Loader2Icon
} from "lucide-react";
import ViolationImageReport from "../components/ViolationImageReport";

const STATUS_OPTIONS = [
  { value: "open", label: "Open", color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  { value: "notice_sent", label: "Notice Sent", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  { value: "hearing_scheduled", label: "Hearing Scheduled", color: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
  { value: "resolved", label: "Resolved", color: "bg-green-500/10 text-green-600 border-green-500/20" },
  { value: "escalated", label: "Escalated", color: "bg-red-500/10 text-red-600 border-red-500/20" }
];

const CATEGORY_OPTIONS = [
  { value: "trash", label: "Trash/Debris" },
  { value: "parking", label: "Parking" },
  { value: "exterior_change", label: "Unauthorized Exterior Change" },
  { value: "noise", label: "Noise" },
  { value: "landscaping", label: "Landscaping" },
  { value: "pets", label: "Pets" },
  { value: "signage", label: "Signage" },
  { value: "fencing", label: "Fencing" },
  { value: "structure", label: "Unauthorized Structure" },
  { value: "other", label: "Other" }
];

const NOTICE_TYPES = [
  { value: "courtesy", label: "Courtesy Notice", description: "Friendly reminder, no fine, 14-day cure period", color: "text-blue-600" },
  { value: "first_formal", label: "First Formal Notice", description: "Official warning, no fine, 14-day cure period", color: "text-yellow-600" },
  { value: "second_formal", label: "Second Notice", description: "Final warning before fines, 10-day cure period", color: "text-orange-600" },
  { value: "fine_notice", label: "Fine Notice", description: "Fine assessed, 10-day cure/appeal period", color: "text-red-600" },
  { value: "hearing_notice", label: "Hearing Notice", description: "Formal hearing scheduled, includes hearing rights", color: "text-purple-600" },
  { value: "final_decision", label: "Final Decision", description: "Post-hearing decision with ongoing requirements", color: "text-red-700" }
];

const ViolationsPage = ({ isBoard = false, embedded = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [violations, setViolations] = useState([]);
  const [archivedViolations, setArchivedViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [sendNoticeOpen, setSendNoticeOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [fineSchedule, setFineSchedule] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoAnalysisOpen, setPhotoAnalysisOpen] = useState(false);
  
  const [newViolation, setNewViolation] = useState({
    property_id: "",
    owner_id: "",
    category: "other",
    violation_type: "",
    description: "",
    rule_citations: [],
    fine_amount: 0
  });
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [selectedNoticeType, setSelectedNoticeType] = useState("");
  const [generateAiDraft, setGenerateAiDraft] = useState(true);

  const fetchViolations = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      if (showOverdueOnly) params.append("overdue_only", "true");
      
      const response = await axios.get(`${API}/violations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViolations(response.data);
    } catch (error) {
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedViolations = async () => {
    if (!isBoard) return;
    try {
      const response = await axios.get(`${API}/violations/archived/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedViolations(response.data);
    } catch (error) {
      console.error("Failed to fetch archived violations");
    }
  };

  const fetchPropertiesAndOwners = async () => {
    if (!isBoard) return;
    try {
      const [propsRes, ownersRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/violations/fine-schedule`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProperties(propsRes.data);
      setOwners(ownersRes.data);
      setFineSchedule(scheduleRes.data);
    } catch (error) {
      console.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    fetchViolations();
    fetchPropertiesAndOwners();
    fetchArchivedViolations();
  }, [token, statusFilter, categoryFilter, showOverdueOnly]);

  const handleCreate = async () => {
    if (!newViolation.property_id || !newViolation.owner_id || !newViolation.violation_type || !newViolation.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await axios.post(`${API}/violations`, newViolation, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Notice created. Initial deadline: ${new Date(response.data.initial_deadline).toLocaleDateString()}`);
      setCreateOpen(false);
      setNewViolation({ property_id: "", owner_id: "", category: "other", violation_type: "", description: "", rule_citations: [], fine_amount: 0 });
      fetchViolations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create notice");
    }
  };

  const handleViewDetails = async (violation) => {
    setSelectedViolation(violation);
    setDetailsOpen(true);
    
    // Fetch timeline
    try {
      const response = await axios.get(`${API}/violations/${violation.violation_id}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(response.data.timeline);
    } catch (error) {
      console.error("Failed to fetch timeline");
    }
  };

  const handleGetAiSuggestions = async (violationId) => {
    setLoadingAi(true);
    try {
      const response = await axios.post(`${API}/violations/${violationId}/ai-suggestions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiSuggestions(response.data);
      toast.success("AI suggestions loaded");
    } catch (error) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setLoadingAi(false);
    }
  };

  const handleSendNotice = async () => {
    if (!selectedNoticeType || !selectedViolation) {
      toast.error("Please select a notice type");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/violations/${selectedViolation.violation_id}/send-notice?notice_type=${selectedNoticeType}&ai_draft=${generateAiDraft}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      if (response.data.fine_applied > 0) {
        toast.info(`Fine of $${response.data.fine_applied.toFixed(2)} applied to owner's account`);
      }
      if (response.data.correspondence_id) {
        toast.info("Draft correspondence created - review in Correspondence page");
      }
      
      setSendNoticeOpen(false);
      setSelectedNoticeType("");
      fetchViolations();
      
      // Refresh details if open
      if (detailsOpen) {
        const updated = await axios.get(`${API}/violations/${selectedViolation.violation_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedViolation(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notice");
    }
  };

  const handleResolve = async () => {
    if (!resolutionNotes.trim()) {
      toast.error("Please provide resolution notes");
      return;
    }

    try {
      await axios.post(
        `${API}/violations/${selectedViolation.violation_id}/resolve?resolution_notes=${encodeURIComponent(resolutionNotes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notice resolved");
      setResolveOpen(false);
      setResolutionNotes("");
      fetchViolations();
      setDetailsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resolve notice");
    }
  };

  const handleArchiveViolation = async (violationId) => {
    try {
      await axios.put(
        `${API}/violations/${violationId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notice archived successfully");
      fetchViolations();
      fetchArchivedViolations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive notice");
    }
  };

  const handleRestoreViolation = async (violationId) => {
    try {
      await axios.put(
        `${API}/violations/${violationId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notice restored successfully");
      fetchViolations();
      fetchArchivedViolations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore notice");
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedViolation) return;
    
    try {
      await axios.delete(
        `${API}/violations/${selectedViolation.violation_id}/permanent`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notice permanently deleted");
      setDeleteConfirmOpen(false);
      setSelectedViolation(null);
      fetchArchivedViolations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete notice");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status);
    return statusConfig ? (
      <Badge className={`${statusConfig.color} border`}>{statusConfig.label}</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  const getEscalationBadge = (level) => {
    const notice = NOTICE_TYPES.find(n => n.value === level);
    return notice ? (
      <Badge variant="outline" className={notice.color}>{notice.label}</Badge>
    ) : null;
  };

  const getCategoryLabel = (cat) => {
    const category = CATEGORY_OPTIONS.find(c => c.value === cat);
    return category ? category.label : cat;
  };

  // Content that will be rendered (with or without sidebar)
  const content = (
    <div className={isBoard ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"}>
      {/* Header - only show for non-board users */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/owner")}
                className="text-muted-foreground hover:text-foreground transition-colors duration-200"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">My Notices</span>
                  <span className="text-xs text-muted-foreground">View your notice status</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Title for Board - no header needed with sidebar */}
      {isBoard && (
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <span className="font-serif text-lg font-semibold block">Notices & Enforcement</span>
            <span className="text-xs text-muted-foreground">Manage community compliance</span>
          </div>
        </div>
      )}

      <div className={isBoard ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        {/* Stats Row for Board */}
        {isBoard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.1s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Open</p>
                    <p className="text-2xl font-bold mt-1">{violations.filter(v => v.status !== 'resolved').length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <AlertCircle className="w-5 h-5 text-warning" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.2s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Overdue</p>
                    <p className="text-2xl font-bold text-destructive mt-1">{violations.filter(v => v.is_overdue).length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-5 h-5 text-destructive" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.3s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Fines</p>
                    <p className="text-2xl font-bold mt-1">${violations.reduce((sum, v) => sum + (v.fine_paid ? 0 : v.fine_amount || 0), 0).toFixed(0)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <DollarSign className="w-5 h-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.4s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Archived</p>
                    <p className="text-2xl font-bold text-muted-foreground mt-1">{archivedViolations.length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Archive className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Navigation for Board */}
        {isBoard && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList>
              <TabsTrigger value="active" data-testid="active-notices-tab">
                Active Notices ({violations.length})
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="archived-notices-tab">
                <Archive className="w-4 h-4 mr-2" />
                Archived ({archivedViolations.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Filters - Only show on active tab */}
        {activeTab === "active" && (
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isBoard && (
            <>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button 
                variant={showOverdueOnly ? "default" : "outline"} 
                onClick={() => setShowOverdueOnly(!showOverdueOnly)}
                className="sm:w-auto"
                data-testid="overdue-filter"
              >
                <Clock className="w-4 h-4 mr-2" />
                {showOverdueOnly ? "Showing Overdue" : "Show Overdue Only"}
              </Button>
            </>
          )}
          
          {isBoard && (
            <>
            <Button variant="outline" onClick={() => setPhotoAnalysisOpen(true)} data-testid="photo-analysis-btn">
              <Camera className="w-4 h-4 mr-2" />
              AI Photo Analysis
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-violation-btn" className="bg-primary text-primary-foreground ml-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Notice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif">Report New Notice</DialogTitle>
                  <DialogDescription>Create a new violation record and begin the enforcement workflow</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Property *</Label>
                    <Select
                      value={newViolation.property_id}
                      onValueChange={(v) => setNewViolation({ ...newViolation, property_id: v })}
                    >
                      <SelectTrigger data-testid="violation-property-select">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map((p) => (
                          <SelectItem key={p.property_id} value={p.property_id}>
                            Lot {p.lot_number} - {p.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Owner *</Label>
                    <Select
                      value={newViolation.owner_id}
                      onValueChange={(v) => setNewViolation({ ...newViolation, owner_id: v })}
                    >
                      <SelectTrigger data-testid="violation-owner-select">
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {owners.map((o) => (
                          <SelectItem key={o.owner_id} value={o.owner_id}>
                            {o.name || `${o.first_name} ${o.last_name}`} ({o.email || o.email_primary})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select
                      value={newViolation.category}
                      onValueChange={(v) => setNewViolation({ ...newViolation, category: v })}
                    >
                      <SelectTrigger data-testid="violation-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fineSchedule?.fine_schedule && newViolation.category && (
                      <p className="text-xs text-muted-foreground">
                        Fine schedule: First ${fineSchedule.fine_schedule[newViolation.category]?.first_fine || 50}, 
                        subsequent ${fineSchedule.fine_schedule[newViolation.category]?.subsequent || 100}, 
                        max ${fineSchedule.fine_schedule[newViolation.category]?.max || 500}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Notice Type/Title *</Label>
                    <Input
                      value={newViolation.violation_type}
                      onChange={(e) => setNewViolation({ ...newViolation, violation_type: e.target.value })}
                      placeholder="e.g., Overgrown lawn, Unauthorized fence"
                      data-testid="violation-type-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={newViolation.description}
                      onChange={(e) => setNewViolation({ ...newViolation, description: e.target.value })}
                      placeholder="Describe the violation in detail. Include dates of observation, specific location on property, etc."
                      rows={4}
                      data-testid="violation-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rule Citations (optional)</Label>
                    <Input
                      value={newViolation.rule_citations.join(", ")}
                      onChange={(e) => setNewViolation({ ...newViolation, rule_citations: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                      placeholder="e.g., CC&Rs §4.2, Design Guidelines §3.1"
                      data-testid="violation-citations-input"
                    />
                    <p className="text-xs text-muted-foreground">Comma-separated list of relevant CC&R or guideline sections</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreate} data-testid="violation-submit-btn">Create Notice</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <ViolationImageReport open={photoAnalysisOpen} onOpenChange={setPhotoAnalysisOpen} onViolationCreated={fetchViolations} />
            </>
          )}
        </div>
        )}

        {/* Active Violations List */}
        {activeTab === "active" && (
        <>
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading violations...</div>
        ) : violations.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-muted-foreground">No violations found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {isBoard ? "No violations match your filters" : "Your property is in good standing"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {violations.map((violation) => (
              <Card 
                key={violation.violation_id} 
                data-testid={`violation-${violation.violation_id}`}
                className={`transition-all hover:shadow-md ${violation.is_overdue ? 'border-red-500/50 bg-red-50/30' : ''}`}
              >
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{violation.violation_type}</h3>
                        {getStatusBadge(violation.status)}
                        {getEscalationBadge(violation.escalation_level)}
                        {violation.is_overdue && (
                          <Badge variant="destructive" className="animate-pulse">
                            <Clock className="w-3 h-3 mr-1" />
                            {violation.days_overdue || Math.abs(violation.days_until_deadline)} days overdue
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-muted-foreground mb-3 line-clamp-2">{violation.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm mb-3">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <FileText className="w-4 h-4" />
                          {getCategoryLabel(violation.category)}
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          Created: {new Date(violation.created_at).toLocaleDateString()}
                        </div>
                        {violation.current_deadline && (
                          <div className={`flex items-center gap-2 ${violation.is_overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                            <Clock className="w-4 h-4" />
                            Deadline: {new Date(violation.current_deadline).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        {violation.property_info && (
                          <span className="text-muted-foreground">
                            📍 Lot {violation.property_info.lot_number}
                          </span>
                        )}
                        {violation.owner_info && (
                          <span className="text-muted-foreground">
                            👤 {violation.owner_info.first_name} {violation.owner_info.last_name}
                          </span>
                        )}
                        {violation.fine_amount > 0 && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-red-500" />
                            <span className="font-medium">${violation.fine_amount.toFixed(2)}</span>
                            {violation.fine_paid ? (
                              <Badge variant="outline" className="text-green-600">Paid</Badge>
                            ) : (
                              <Badge variant="outline" className="text-red-600">Unpaid</Badge>
                            )}
                          </div>
                        )}
                        {violation.notices_sent?.length > 0 && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Send className="w-4 h-4" />
                            {violation.notices_sent.length} notice(s) sent
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(violation)}
                        data-testid={`view-details-${violation.violation_id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Details
                      </Button>
                      
                      {isBoard && violation.status !== "resolved" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedViolation(violation);
                              setSendNoticeOpen(true);
                            }}
                            data-testid={`send-notice-${violation.violation_id}`}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Send Notice
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => {
                              setSelectedViolation(violation);
                              setResolveOpen(true);
                            }}
                            data-testid={`resolve-${violation.violation_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Resolve
                          </Button>
                        </>
                      )}
                      
                      {/* Archive button for resolved notices */}
                      {isBoard && violation.status === "resolved" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArchiveViolation(violation.violation_id)}
                          data-testid={`archive-${violation.violation_id}`}
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Archive
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {/* Archived Violations Tab Content */}
        {isBoard && activeTab === "archived" && (
          <div className="space-y-4">
            {archivedViolations.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No archived notices</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Resolved notices can be archived to keep your active list clean
                  </p>
                </CardContent>
              </Card>
            ) : (
              archivedViolations.map((violation) => (
                <Card 
                  key={violation.violation_id}
                  data-testid={`archived-violation-${violation.violation_id}`}
                  className="transition-all hover:shadow-md bg-muted/30"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{violation.violation_type}</h3>
                          <Badge className="bg-muted text-muted-foreground border">Archived</Badge>
                          {getEscalationBadge(violation.escalation_level)}
                        </div>
                        
                        <p className="text-muted-foreground mb-3 line-clamp-2">{violation.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm mb-3">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <FileText className="w-4 h-4" />
                            {getCategoryLabel(violation.category)}
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            Archived: {violation.archived_at ? new Date(violation.archived_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                          {violation.property_info && (
                            <span className="text-muted-foreground">
                              📍 Lot {violation.property_info.lot_number}
                            </span>
                          )}
                          {violation.owner_info && (
                            <span className="text-muted-foreground">
                              👤 {violation.owner_info.first_name} {violation.owner_info.last_name}
                            </span>
                          )}
                          {violation.fine_amount > 0 && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <span>${violation.fine_amount.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreViolation(violation.violation_id)}
                          data-testid={`restore-${violation.violation_id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Restore
                        </Button>
                        
                        {user?.role === "admin" && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setSelectedViolation(violation);
                              setDeleteConfirmOpen(true);
                            }}
                            data-testid={`delete-${violation.violation_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* View Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Notice Details</DialogTitle>
            </DialogHeader>
            {selectedViolation && (
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="timeline">Timeline</TabsTrigger>
                  <TabsTrigger value="ai">AI Suggestions</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <div className="mt-1">{getStatusBadge(selectedViolation.status)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Escalation Level</Label>
                      <div className="mt-1">{getEscalationBadge(selectedViolation.escalation_level)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Category</Label>
                      <p className="font-medium">{getCategoryLabel(selectedViolation.category)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Type</Label>
                      <p className="font-medium">{selectedViolation.violation_type}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-muted-foreground">Description</Label>
                      <p>{selectedViolation.description}</p>
                    </div>
                    {selectedViolation.property_info && (
                      <div>
                        <Label className="text-muted-foreground">Property</Label>
                        <p>Lot {selectedViolation.property_info.lot_number} - {selectedViolation.property_info.address}</p>
                      </div>
                    )}
                    {selectedViolation.owner_info && (
                      <div>
                        <Label className="text-muted-foreground">Owner</Label>
                        <p>{selectedViolation.owner_info.first_name} {selectedViolation.owner_info.last_name}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">Current Deadline</Label>
                      <p className={selectedViolation.is_overdue ? 'text-red-600 font-medium' : ''}>
                        {selectedViolation.current_deadline ? new Date(selectedViolation.current_deadline).toLocaleDateString() : 'Not set'}
                        {selectedViolation.is_overdue && ' (OVERDUE)'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Fine Amount</Label>
                      <p className="font-medium">${(selectedViolation.fine_amount || 0).toFixed(2)}</p>
                    </div>
                    {selectedViolation.rule_citations?.length > 0 && (
                      <div className="col-span-2">
                        <Label className="text-muted-foreground">Rule Citations</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedViolation.rule_citations.map((citation, i) => (
                            <Badge key={i} variant="outline">{citation}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedViolation.resolution_notes && (
                      <div className="col-span-2">
                        <Label className="text-muted-foreground">Resolution Notes</Label>
                        <p className="text-green-700">{selectedViolation.resolution_notes}</p>
                      </div>
                    )}
                  </div>
                  
                  {isBoard && selectedViolation.status !== 'resolved' && (
                    <div className="space-y-3 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Suggested next step: <strong>{NOTICE_TYPES.find(n => n.value === selectedViolation.suggested_next_notice)?.label || 'None'}</strong>
                        {selectedViolation.suggested_fine > 0 && ` (Fine: $${selectedViolation.suggested_fine.toFixed(2)})`}
                      </p>
                      <AutoEscalateButton
                        violationId={selectedViolation.violation_id}
                        token={token}
                        onEscalated={async () => {
                          const updated = await axios.get(`${API}/violations/${selectedViolation.violation_id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSelectedViolation(updated.data);
                          fetchViolations();
                        }}
                      />
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="timeline" className="mt-4">
                  {timeline.length > 0 ? (
                    <div className="space-y-4">
                      {timeline.map((event, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full ${
                              event.event_type === 'created' ? 'bg-blue-500' :
                              event.event_type === 'resolved' ? 'bg-green-500' :
                              'bg-yellow-500'
                            }`} />
                            {index < timeline.length - 1 && <div className="w-0.5 h-full bg-gray-200 mt-2" />}
                          </div>
                          <div className="flex-1 pb-4">
                            <p className="font-medium">{event.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(event.timestamp).toLocaleString()}
                            </p>
                            {event.deadline && (
                              <p className="text-sm">Deadline: {new Date(event.deadline).toLocaleDateString()}</p>
                            )}
                            {event.fine_amount > 0 && (
                              <p className="text-sm text-red-600">Fine: ${event.fine_amount.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No timeline events</p>
                  )}
                </TabsContent>
                
                <TabsContent value="ai" className="mt-4">
                  {!aiSuggestions ? (
                    <div className="text-center py-8">
                      <Sparkles className="w-12 h-12 text-primary mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground mb-4">Get AI-powered suggestions for handling this violation</p>
                      <Button 
                        onClick={() => handleGetAiSuggestions(selectedViolation.violation_id)}
                        disabled={loadingAi}
                      >
                        {loadingAi ? "Loading..." : "Get AI Suggestions"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {aiSuggestions.suggestions?.rule_citations && (
                        <div>
                          <Label className="text-muted-foreground">Suggested Rule Citations</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {aiSuggestions.suggestions.rule_citations.map((citation, i) => (
                              <Badge key={i} variant="outline">{citation}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {aiSuggestions.suggestions?.cure_period_days && (
                        <div>
                          <Label className="text-muted-foreground">Suggested Cure Period</Label>
                          <p>{aiSuggestions.suggestions.cure_period_days} days</p>
                        </div>
                      )}
                      {aiSuggestions.suggestions?.suggested_fine > 0 && (
                        <div>
                          <Label className="text-muted-foreground">Suggested Fine</Label>
                          <p className="font-medium">${aiSuggestions.suggestions.suggested_fine.toFixed(2)}</p>
                        </div>
                      )}
                      {aiSuggestions.suggestions?.key_points && (
                        <div>
                          <Label className="text-muted-foreground">Key Points to Include</Label>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            {aiSuggestions.suggestions.key_points.map((point, i) => (
                              <li key={i} className="text-sm">{point}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiSuggestions.suggestions?.draft_notice && (
                        <div>
                          <Label className="text-muted-foreground">Draft Notice</Label>
                          <div className="mt-2 p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
                            {aiSuggestions.suggestions.draft_notice}
                          </div>
                        </div>
                      )}
                      <Button 
                        variant="outline" 
                        onClick={() => setAiSuggestions(null)}
                        className="w-full"
                      >
                        Refresh Suggestions
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Notice Dialog */}
        <Dialog open={sendNoticeOpen} onOpenChange={setSendNoticeOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                <Send className="w-5 h-5" />
                Send Notice
              </DialogTitle>
              <DialogDescription>
                Select the notice type to send. This will escalate the violation and set a new deadline.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                {NOTICE_TYPES.map((notice) => (
                  <div 
                    key={notice.value}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedNoticeType === notice.value 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:border-gray-400'
                    }`}
                    onClick={() => setSelectedNoticeType(notice.value)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-medium ${notice.color}`}>{notice.label}</p>
                        <p className="text-sm text-muted-foreground">{notice.description}</p>
                      </div>
                      {selectedNoticeType === notice.value && (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center gap-2 pt-4 border-t">
                <input
                  type="checkbox"
                  id="ai-draft"
                  checked={generateAiDraft}
                  onChange={(e) => setGenerateAiDraft(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="ai-draft" className="text-sm cursor-pointer">
                  Generate AI draft for correspondence
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendNoticeOpen(false)}>Cancel</Button>
              <Button onClick={handleSendNotice} disabled={!selectedNoticeType}>
                <Send className="w-4 h-4 mr-2" />
                Send {NOTICE_TYPES.find(n => n.value === selectedNoticeType)?.label || 'Notice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resolve Dialog */}
        <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Resolve Notice
              </DialogTitle>
              <DialogDescription>
                Mark this violation as resolved. Please provide notes about how the violation was corrected.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Resolution Notes *</Label>
                <Textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how the violation was resolved. Include dates of inspection, any follow-up actions, etc."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResolveOpen(false)}>Cancel</Button>
              <Button onClick={handleResolve} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolve Notice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permanent Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Permanent Delete
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. This will permanently delete the notice and all associated evidence.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {selectedViolation && (
                <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                  <p className="font-medium">{selectedViolation.violation_type}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedViolation.property_info?.lot_number && `Lot ${selectedViolation.property_info.lot_number}`}
                    {selectedViolation.owner_info && ` • ${selectedViolation.owner_info.first_name} ${selectedViolation.owner_info.last_name}`}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handlePermanentDelete} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  // Wrap with AdminSidebar for board mode
  if (embedded) {
    return content;
  }
  if (isBoard) {
    return <AdminSidebar>{content}</AdminSidebar>;
  }

  return content;
};

export default ViolationsPage;


// Auto-Escalate button component for violation detail
const AutoEscalateButton = ({ violationId, token, onEscalated }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const fetchPreview = async () => {
    try {
      const res = await axios.get(`${API}/violations/${violationId}/escalation-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(res.data);
      setShowPreview(true);
    } catch { toast.error("Failed to preview escalation"); }
  };

  const handleEscalate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/violations/${violationId}/auto-escalate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setShowPreview(false);
      onEscalated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Escalation failed");
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={fetchPreview}
        data-testid="auto-escalate-btn"
      >
        <ArrowUpCircle className="w-4 h-4 mr-1" /> Auto-Escalate
      </Button>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ArrowUpCircle className="w-5 h-5" /> Confirm Escalation
            </DialogTitle>
            <DialogDescription>
              This will automatically escalate the violation to the next level with an AI-drafted notice.
            </DialogDescription>
          </DialogHeader>
          {preview && preview.can_escalate && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-2 bg-muted rounded">
                  <p className="text-muted-foreground">Current Level</p>
                  <p className="font-medium capitalize">{preview.current_level?.replace(/_/g, ' ')}</p>
                </div>
                <div className="p-2 bg-red-50 rounded border border-red-200">
                  <p className="text-muted-foreground">Next Level</p>
                  <p className="font-medium capitalize text-red-700">{preview.next_level?.replace(/_/g, ' ')}</p>
                </div>
                <div className="p-2 bg-muted rounded">
                  <p className="text-muted-foreground">Fine Amount</p>
                  <p className="font-medium">${preview.fine_amount?.toFixed(2)}</p>
                </div>
                <div className="p-2 bg-muted rounded">
                  <p className="text-muted-foreground">Cure Period</p>
                  <p className="font-medium">{preview.cure_days} days</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Total fines after escalation: ${preview.total_fines_after?.toFixed(2)}
              </p>
            </div>
          )}
          {preview && !preview.can_escalate && (
            <p className="text-sm text-muted-foreground">{preview.message}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Cancel</Button>
            {preview?.can_escalate && (
              <Button variant="destructive" onClick={handleEscalate} disabled={loading} data-testid="confirm-escalate-btn">
                {loading ? <Loader2Icon className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpCircle className="w-4 h-4 mr-2" />}
                Escalate Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
